import api, { apiRequest } from './api';

export interface DiciRegistro {
    fistel: string;
    cnpj_escritorio: string;
    escritorio_nome: string;
    tipo_cliente: string;
    tipo_atendimento: string;
    tipo_meio: string;
    faixa_velocidade: string;
    tipo_produto: string;
    quantidade: number;
    uf: string;
    municipio: string;
    servicos_detalhes?: {
        cliente_nome: string;
        servico_id: number;
        contrato_id: number;
    }[];
}

export interface DiciEstatisticas {
    total_acessos: number;
    por_tipo_cliente: { tipo: string; quantidade: number }[];
    por_tipo_meio: { tipo: string; quantidade: number }[];
    por_tipo_atendimento: { tipo: string; quantidade: number }[];
    por_tipo_produto: { tipo: string; quantidade: number }[];
    por_faixa_velocidade: { faixa: string; quantidade: number }[];
}

export interface DiciResponse<T> {
    success: boolean;
    data: T;
    meta: {
        mes_referencia: string;
        escritorio_id: number | null;
        somente_dici: boolean;
        total_registros?: number;
        total_acessos?: number;
    };
}

export interface DiciGeradoItem {
    id: number;
    mes_referencia: string;
    escritorio_id: number | null;
    usuario_id: number;
    total_registros: number;
    total_acessos: number;
    somente_dici: boolean;
    atrelar_nfcom: boolean;
    dados_consolidados: DiciRegistro[];
    observacoes: string | null;
    createdAt: string;
    updatedAt: string;
    usuario?: { id: number; nome: string };
    escritorio?: { id: number; nome_fantasia: string; razao_social: string; fistel_anatel: string };
}

const diciService = {
    getPreview: async (mes: string, escritorioId?: number, somenteDici: boolean = true, atrelarNfcom: boolean = false): Promise<DiciResponse<DiciRegistro[]>> => {
        const params = new URLSearchParams();
        params.append('mes', mes);
        if (escritorioId) params.append('escritorio_id', escritorioId.toString());
        params.append('somente_dici', somenteDici.toString());
        params.append('atrelar_nfcom', atrelarNfcom.toString());

        const response = await apiRequest.get<DiciResponse<DiciRegistro[]>>(`/dici/preview?${params.toString()}`);
        return response as any;
    },

    getEstatisticas: async (mes: string, escritorioId?: number, somenteDici: boolean = true, atrelarNfcom: boolean = false): Promise<DiciResponse<DiciEstatisticas>> => {
        const params = new URLSearchParams();
        params.append('mes', mes);
        if (escritorioId) params.append('escritorio_id', escritorioId.toString());
        params.append('somente_dici', somenteDici.toString());
        params.append('atrelar_nfcom', atrelarNfcom.toString());

        const response = await apiRequest.get<DiciResponse<DiciEstatisticas>>(`/dici/estatisticas?${params.toString()}`);
        return response as any;
    },

    gerarCsv: async (mes: string, escritorioId?: number, somenteDici: boolean = true, atrelarNfcom: boolean = false): Promise<void> => {
        const params = new URLSearchParams();
        params.append('mes', mes);
        if (escritorioId) params.append('escritorio_id', escritorioId.toString());
        params.append('somente_dici', somenteDici.toString());
        params.append('atrelar_nfcom', atrelarNfcom.toString());

        // A instância 'api' possui um interceptor que retorna apenas o 'response.data'.
        // Portanto, a variável 'response' aqui já é o Blob diretamente.
        const response: any = await api.get(`/dici/gerar?${params.toString()}`, {
            responseType: 'blob'
        });

        if (!response) {
            throw new Error('Não foi possível obter o arquivo do servidor.');
        }

        // Como o interceptor já extraiu o .data, 'response' é o Blob.
        // Criar link de download
        const blob = response instanceof Blob ? response : new Blob([response]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Fallback do nome do arquivo (já que o interceptor remove os headers)
        const filename = `DICI_SCM_${mes}${escritorioId ? `_escritorio_${escritorioId}` : ''}.csv`;

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        // Limpeza
        setTimeout(() => {
            link.remove();
            window.URL.revokeObjectURL(url);
        }, 100);
    },

    listarGerados: async (escritorioId?: number, dataInicio?: string, dataFim?: string): Promise<{ success: boolean; data: DiciGeradoItem[] }> => {
        const params = new URLSearchParams();
        if (escritorioId) params.append('escritorio_id', escritorioId.toString());
        if (dataInicio) params.append('data_inicio', dataInicio);
        if (dataFim) params.append('data_fim', dataFim);

        const response = await apiRequest.get<{ success: boolean; data: DiciGeradoItem[] }>(`/dici/gerados?${params.toString()}`);
        return response as any;
    },

    verificarExistente: async (mes: string, escritorioId?: number): Promise<{ success: boolean; existe: boolean; data: DiciGeradoItem | null }> => {
        const params = new URLSearchParams();
        params.append('mes', mes);
        if (escritorioId) params.append('escritorio_id', escritorioId.toString());

        const response = await apiRequest.get<{ success: boolean; existe: boolean; data: DiciGeradoItem | null }>(`/dici/verificar?${params.toString()}`);
        return response as any;
    },

    gravarDici: async (
        mes: string,
        escritorioId?: number,
        somenteDici: boolean = true,
        atrelarNfcom: boolean = false,
        observacoes?: string
    ): Promise<{ success: boolean; message: string; data?: DiciGeradoItem }> => {
        const response = await apiRequest.post<{ success: boolean; message: string; data?: DiciGeradoItem }>('/dici/gravar', {
            mes,
            escritorio_id: escritorioId || null,
            somente_dici: somenteDici,
            atrelar_nfcom: atrelarNfcom,
            observacoes,
        });
        return response as any;
    },

    excluirDici: async (id: number): Promise<{ success: boolean; message: string }> => {
        const response = await apiRequest.delete<{ success: boolean; message: string }>(`/dici/gerados/${id}`);
        return response as any;
    },
};

export default diciService;
